const { DateTime } = require("luxon");

const PACIFIC_TIME_ZONE = "America/Los_Angeles";
const MARKET_SCHEDULE_PATH = "/api/public/market-schedule";
const DEVELOPMENT_API_BASE_URL = "http://localhost:3000/";
const VALID_EVENT_TYPES = new Set(["market", "festival", "expo", "pop_up", "other"]);

function cleanString(value) {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length ? trimmed : null;
}

function cleanTime(value) {
  const trimmed = cleanString(value);

  if (!trimmed || !/^\d{2}:\d{2}$/.test(trimmed)) {
    return null;
  }

  const parsed = DateTime.fromFormat(trimmed, "HH:mm", { zone: PACIFIC_TIME_ZONE });
  return parsed.isValid ? trimmed : null;
}

function cleanDate(value) {
  const trimmed = cleanString(value);

  if (!trimmed || !/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return null;
  }

  const parsed = DateTime.fromISO(trimmed, { zone: PACIFIC_TIME_ZONE });
  return parsed.isValid ? trimmed : null;
}

function normalizeMarketScheduleEvent(entry) {
  if (!entry || typeof entry !== "object") {
    return null;
  }

  const eventId = Number(entry.eventId);
  const eventDayId = Number(entry.eventDayId);
  const date = cleanDate(entry.date);
  const title = cleanString(entry.title);

  if (!Number.isFinite(eventId) || !Number.isFinite(eventDayId) || !date || !title) {
    return null;
  }

  const type = VALID_EVENT_TYPES.has(entry.type) ? entry.type : "other";

  return {
    eventId,
    eventDayId,
    type,
    title,
    summary: cleanString(entry.summary),
    locationName: cleanString(entry.locationName),
    address: cleanString(entry.address),
    mapUrl: cleanString(entry.mapUrl),
    date,
    openTime: cleanTime(entry.openTime),
    closeTime: cleanTime(entry.closeTime),
    featured: Boolean(entry.featured),
    sortOrder: Number.isFinite(Number(entry.sortOrder)) ? Number(entry.sortOrder) : 0,
  };
}

function normalizeMarketScheduleResponse(data) {
  if (!Array.isArray(data)) {
    return [];
  }

  return data.map(normalizeMarketScheduleEvent).filter(Boolean);
}

function buildMarketScheduleUrl(baseUrl, limit) {
  const url = new URL(MARKET_SCHEDULE_PATH, baseUrl);
  const normalizedLimit = Number(limit);

  if (Number.isFinite(normalizedLimit) && normalizedLimit > 0) {
    url.searchParams.set("limit", String(Math.floor(normalizedLimit)));
  }

  return url;
}

function getMarketScheduleBaseUrl(options = {}) {
  if (options.baseUrl) {
    return options.baseUrl;
  }

  if (process.env.BT_PROD_API_BASE_URL) {
    return process.env.BT_PROD_API_BASE_URL;
  }

  if (process.env.NODE_ENV === "development") {
    return DEVELOPMENT_API_BASE_URL;
  }

  return "";
}

async function getMarketSchedule(limit, options = {}) {
  const baseUrl = getMarketScheduleBaseUrl(options);
  const fetchImpl = options.fetchImpl || global.fetch;
  const logger = options.logger || console;
  const context = cleanString(options.context) || "unknown";

  if (!baseUrl) {
    if (logger && typeof logger.warn === "function") {
      logger.warn(`[market-schedule] Skipping fetch for ${context}: missing BT_PROD_API_BASE_URL`);
    }
    return [];
  }

  if (typeof fetchImpl !== "function") {
    if (logger && typeof logger.warn === "function") {
      logger.warn(`[market-schedule] Skipping fetch for ${context}: fetch implementation is unavailable`);
    }
    return [];
  }

  try {
    const url = buildMarketScheduleUrl(baseUrl, limit);
    if (logger && typeof logger.info === "function") {
      logger.info(`[market-schedule] Fetching for ${context} from ${url.toString()}`);
    }
    const res = await fetchImpl(url.toString());

    if (!res || !res.ok) {
      if (logger && typeof logger.warn === "function") {
        logger.warn(
          `[market-schedule] Non-OK response for ${context}`,
          { status: res?.status, statusText: res?.statusText, url: url.toString() }
        );
      }
      return [];
    }

    const data = await res.json();
    const events = normalizeMarketScheduleResponse(data);

    if (logger && typeof logger.info === "function") {
      logger.info(`[market-schedule] Received ${events.length} events for ${context}`);
    }

    return events;
  } catch (error) {
    if (logger && typeof logger.warn === "function") {
      logger.warn(`[market-schedule] Failed to fetch for ${context}`, error);
    }

    return [];
  }
}

function parsePacificDate(date) {
  return DateTime.fromISO(date, { zone: PACIFIC_TIME_ZONE }).startOf("day");
}

function getPacificToday(now = new Date()) {
  return DateTime.fromJSDate(now, { zone: PACIFIC_TIME_ZONE }).toISODate();
}

function isEventToday(event, now = new Date()) {
  return Boolean(event && event.date === getPacificToday(now));
}

function formatEventDate(date) {
  const parsed = parsePacificDate(date);
  return parsed.isValid ? parsed.toFormat("ccc, LLL d") : "";
}

function formatClockTime(value) {
  const time = cleanTime(value);

  if (!time) {
    return "";
  }

  return DateTime.fromFormat(time, "HH:mm", { zone: PACIFIC_TIME_ZONE }).toFormat("h:mm a");
}

function formatEventTime(openTime, closeTime) {
  const open = formatClockTime(openTime);
  const close = formatClockTime(closeTime);

  if (open && close) {
    return `${open}-${close}`;
  }

  if (open) {
    return `Starts ${open}`;
  }

  if (close) {
    return `Until ${close}`;
  }

  return "Hours TBA";
}

function splitEventsByWeek(events, now = new Date()) {
  const sourceEvents = Array.isArray(events) ? events : [];
  const today = parsePacificDate(getPacificToday(now));
  const weekEnd = today.plus({ days: 7 });

  return sourceEvents.reduce(
    (groups, event) => {
      const eventDate = parsePacificDate(event.date);

      if (eventDate.isValid && eventDate >= today && eventDate < weekEnd) {
        groups.thisWeek.push(event);
      } else {
        groups.comingUp.push(event);
      }

      return groups;
    },
    { thisWeek: [], comingUp: [] }
  );
}

module.exports = {
  DEVELOPMENT_API_BASE_URL,
  MARKET_SCHEDULE_PATH,
  PACIFIC_TIME_ZONE,
  buildMarketScheduleUrl,
  formatClockTime,
  formatEventDate,
  formatEventTime,
  getMarketSchedule,
  getMarketScheduleBaseUrl,
  getPacificToday,
  isEventToday,
  normalizeMarketScheduleEvent,
  normalizeMarketScheduleResponse,
  splitEventsByWeek,
};
